import { AUTHORING_GUIDE_FILES, authoringGuideUrl } from "../utils/authoringGuideExport";

// The authoring help chat answers from the activity JSON authoring guide (docs/activity-json/AUTHORING_GUIDE.md,
// served at /activity-json/ by `npm run generate-activity-reference`). The whole guide goes with the first question of
// a conversation; follow-up questions chain on the previous response, which already holds it. Every question also gets
// the REFERENCE.md rows for the item types and keys it names, since the full reference is too large to send.
export const MAX_AUTHORING_GUIDE_CHARS = 60000;
export const MAX_REFERENCE_EXCERPT_CHARS = 12000;
const MAX_TYPE_SECTION_CHARS = 5000;

let cachedDocuments = null;

// The guide and reference as served by this Cloud build. A file that cannot be loaded comes back as "" so the chat
// still answers from the rest of its context.
export function loadAuthoringGuideDocuments({ fetchImpl = typeof fetch === "function" ? fetch : null, forceRefresh = false } = {}) {
	if (cachedDocuments && !forceRefresh) {
		return cachedDocuments;
	}
	const load = async (fileName) => {
		if (!fetchImpl) {
			return "";
		}
		try {
			const response = await fetchImpl(authoringGuideUrl(fileName), { cache: "no-store" });
			return response && response.ok ? await response.text() : "";
		} catch (err) {
			console.warn(`Unable to load ${fileName} for the authoring assistant`, err);
			return "";
		}
	};
	const pending = Promise.all([load(AUTHORING_GUIDE_FILES.guide), load(AUTHORING_GUIDE_FILES.reference)])
		.then(([guide, reference]) => {
			if (!guide || !reference) {
				cachedDocuments = null; // try again on the next question
			}
			return { guide, reference };
		});
	cachedDocuments = pending;
	return pending;
}

export function resetAuthoringGuideDocuments() {
	cachedDocuments = null;
}

function tableCells(line) {
	return line.split("|").slice(1, -1).map((cell) => cell.trim());
}

function cellKey(cell = "") {
	const match = /^`([^`]+)`$/.exec(cell.trim());
	return match ? match[1] : "";
}

// REFERENCE.md as item type sections ("### `itemPicker`" under "Type-specific keys per item type") and key rows from
// every table (the first cell is the key in backticks, the second its authoring name).
export function parseAuthoringReference(markdown = "") {
	const typeSections = {};
	const rows = [];
	let heading = "";
	let typeName = "";
	let typeLines = null;
	let tableHeader = null;
	const closeType = () => {
		if (typeName && typeLines) {
			typeSections[typeName] = typeLines.join("\n").trim();
		}
		typeName = "";
		typeLines = null;
	};
	String(markdown).split(/\r?\n/).forEach((line) => {
		const headingMatch = /^(#{2,3}) (.*)$/.exec(line);
		if (headingMatch) {
			closeType();
			heading = headingMatch[2].trim();
			tableHeader = null;
			const typeMatch = headingMatch[1] === "###" ? /^`([A-Za-z0-9]+)`$/.exec(heading) : null;
			if (typeMatch) {
				typeName = typeMatch[1];
				typeLines = [line];
			}
			return;
		}
		if (typeLines) {
			typeLines.push(line);
		}
		if (!line.startsWith("|")) {
			tableHeader = null;
			return;
		}
		if (!tableHeader) {
			tableHeader = [line];
			return;
		}
		if (tableHeader.length === 1) {
			tableHeader.push(line);
			return;
		}
		const cells = tableCells(line);
		const key = cellKey(cells[0]);
		if (key) {
			rows.push({ heading, header: tableHeader, key, authoringName: cells[1] || "", line });
		}
	});
	closeType();
	return { typeSections, rows };
}

function escapeRegExp(text) {
	return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function mentions(questionLower, phrase) {
	const needle = String(phrase || "").trim().toLowerCase();
	return needle !== "" && new RegExp(`(^|[^a-z0-9])${escapeRegExp(needle)}($|[^a-z0-9])`).test(questionLower);
}

// A key counts as named when it is specific enough to mean itself (camelCase, a digit, six or more letters) or the
// question puts it in backticks; "id", "type" or "value" alone would pull rows into almost every question.
function isSpecificKey(key, questionLower) {
	return /[A-Z0-9]/.test(key) || key.length >= 6 || questionLower.includes(`\`${key.toLowerCase()}\``);
}

// The reference rows for what the question names: whole sections for the item types it mentions (by type, such as
// itemPicker, or by the authoring tool's name, such as "Picker"), plus the rows for each key it mentions (by key, such
// as pickerItems, or by a multi-word authoring name, such as "Advance On Select"), grouped under their tables.
export function selectAuthoringReferenceExcerpt(parsedReference, question = "", itemTypes = [], { maxChars = MAX_REFERENCE_EXCERPT_CHARS } = {}) {
	if (!parsedReference || !question) {
		return "";
	}
	const questionLower = String(question).toLowerCase();
	const parts = [];
	let length = 0;
	const add = (text) => {
		if (!text || length + text.length + 2 > maxChars) {
			return false;
		}
		parts.push(text);
		length += text.length + 2;
		return true;
	};

	const typeSections = parsedReference.typeSections || {};
	// Where the question first names each item type; types come out in that order so the first named wins the space.
	const namedAt = new Map();
	const name = (type, position) => {
		if (!namedAt.has(type) || position < namedAt.get(type)) {
			namedAt.set(type, position);
		}
	};
	// "item" is in nearly every question, so only its authoring name ("Check Item") names that type.
	Object.keys(typeSections).filter((type) => type !== "item" && mentions(questionLower, type)).forEach((type) => name(type, questionLower.indexOf(type.toLowerCase())));
	// Authoring names longest first, each claiming its words, so "Date Picker" does not also name "Picker".
	let unclaimed = questionLower;
	(itemTypes || [])
		.filter((entry) => entry && typeSections[entry.type] && typeof entry.name === "string")
		.map((entry) => ({ type: entry.type, label: entry.name.trim().toLowerCase() }))
		.filter(({ type, label }) => label.length >= 4 && (type !== "item" || label.split(/\s+/).length >= 2))
		.sort((a, b) => b.label.length - a.label.length)
		.forEach(({ type, label }) => {
			if (mentions(unclaimed, label)) {
				name(type, unclaimed.indexOf(label));
				unclaimed = unclaimed.split(label).join(" ".repeat(label.length));
			}
		});
	const namedTypes = [...namedAt.keys()].sort((a, b) => namedAt.get(a) - namedAt.get(b));
	const shownKeys = new Set();
	namedTypes.forEach((type) => {
		const section = typeSections[type];
		if (add(section.length > MAX_TYPE_SECTION_CHARS ? `${section.slice(0, MAX_TYPE_SECTION_CHARS)}\n[section truncated]` : section)) {
			(parsedReference.rows || []).filter((row) => row.heading === `\`${type}\``).forEach((row) => shownKeys.add(row.key));
		}
	});

	const byTable = new Map();
	(parsedReference.rows || []).forEach((row) => {
		const authoringName = row.authoringName || "";
		const namedByKey = isSpecificKey(row.key, questionLower) && mentions(questionLower, row.key);
		const namedByName = authoringName.split(/\s+/).length >= 2 && mentions(questionLower, authoringName);
		if (!namedByKey && !namedByName) {
			return;
		}
		if (shownKeys.has(row.key)) {
			return; // already in an item type section above
		}
		const tableKey = `${row.heading}\n${row.header.join("\n")}`;
		if (!byTable.has(tableKey)) {
			byTable.set(tableKey, { row, lines: [] });
		}
		byTable.get(tableKey).lines.push(row.line);
	});
	byTable.forEach(({ row, lines }) => {
		add([`From "${row.heading}":`, ...row.header, ...lines].join("\n"));
	});

	return parts.join("\n\n");
}

// The guide and reference sections the authoring chat adds after its usual context and before the author's question.
export function buildAuthoringGuideSections({ question = "", guide = "", reference = null, itemTypes = [], includeGuide = false } = {}) {
	const sections = [];
	if (includeGuide && guide) {
		const text = guide.length > MAX_AUTHORING_GUIDE_CHARS ? `${guide.slice(0, MAX_AUTHORING_GUIDE_CHARS)}\n\n[Guide truncated]` : guide;
		sections.push([
			"AmbiFi activity JSON authoring guide (docs/activity-json/AUTHORING_GUIDE.md), the authoritative description of activity JSON. Answer from it and follow its rules; say so when it does not cover a question instead of guessing.",
			"<authoring_guide>",
			text.trim(),
			"</authoring_guide>"
		].join("\n"));
	}
	const excerpt = selectAuthoringReferenceExcerpt(reference, question, itemTypes);
	if (excerpt) {
		sections.push([
			"Reference rows for what this question names (docs/activity-json/REFERENCE.md: keys, authoring names, defaults and values seen in real activities):",
			excerpt
		].join("\n"));
	}
	return sections;
}
